import {
    ComponentElements,
    ComponentInjector,
    ComponentObject,
    ComponentSelector,
    ComponentSelectors,
    ComponentService
} from '@atypon/ui-utils';
import {keyCodes} from '../../../utility/js/utils';

class Texts extends ComponentObject { 
    add = 'Add search field';
    remove = 'Remove search field';
}

class ClassList extends ComponentObject {
    keywordField = 'text';
    typeField = 'searchArea';
    hide = 'hidden';
}

class Selectors extends ComponentSelectors {
    searchInFields = '.advanced-search__search-in';
    add = '.advanced-search__search-in__add';
    remove = '.advanced-search__search-in__remove';
    keywordField = 'input';
    typeField = 'select';
    searchInFieldWrapper = new ComponentSelector('.advanced-search__search-in__term');
}

class Elements extends ComponentElements<Selectors> {
    protected _searchInField: HTMLElement;
    searchInFieldWrapper: HTMLElement[] = [];
    currentAddButton: HTMLElement;
    currentRemoveButton: HTMLElement;
    wrapper: HTMLElement;

    set searchInField(searchInField: HTMLElement) {
        this._searchInField = searchInField;
        this.currentAddButton = this.getAddButton(searchInField);
        this.currentRemoveButton = this.getRemoveButton(searchInField);
    }

    get searchInField(): HTMLElement {
        return this._searchInField;
    }

    /**
     * returns the add action
     *
     * @param searchInField
     * @return {HTMLElement}
     */
    getAddButton(searchInField): HTMLElement {
        return this.domUtils.getElement(this.selectors.add, searchInField);
    }

    /**
     * returns the remove action
     *
     * @param searchInField
     * @return {HTMLElement}
     */
    getRemoveButton(searchInField): HTMLElement {
        return this.domUtils.getElement(this.selectors.remove, searchInField);
    }

    /**
     * returns all search in fields,
     * this is needed so we can have the updated DOM list and update their name attributes
     *
     * @return {Array<HTMLElement>}
     */
    get searchInFields(): HTMLElement[] {
        return this.domUtils.getElements(this.selectors.searchInFieldWrapper.selector, this.wrapper);
    }

}

interface AdvancedSearchTabular extends ComponentService<Selectors, Elements, ClassList, Texts> { }

@ComponentInjector(Selectors, Elements, ClassList, Texts)
class AdvancedSearchTabular {

    /**
     * Needed for tabular field names
     *
     * @return {{input: string, dropdown: string}}
     */
    get fieldNames() {
        return {
            input: 'text',
            dropdown: 'field',
        };
    }

    constructor(readonly wrapper: HTMLElement, readonly onChanged: (Event) => void) {
    }

    initialize(): void {
        this.elements.initialize(this.wrapper);
        this.addEventListeners();
        this.toggleButtons();
    }


    addEventListeners() {
        if (this.elements.searchInField) {
            this.setSearchInFieldsEvents(this.elements.searchInField);
            return;
        }
        this.elements.searchInFieldWrapper.forEach(elm => {
            this.elements.currentAddButton = this.elements.getAddButton(elm);
            this.elements.currentRemoveButton = this.elements.getRemoveButton(elm);
            this.setSearchInFieldsEvents(elm);
        })
    }

    protected setSearchInFieldsEvents(searchInField: HTMLElement) {
        this.domUtils.addEventListener(searchInField, 'input', this.onChanged.bind(this));
        this.domUtils.addEventListener(this.elements.currentAddButton, 'click', this.clone.bind(this));
        this.domUtils.addEventListener(this.elements.currentRemoveButton, 'click', this.remove.bind(this));
        this.domUtils.addEventListener(this.elements.currentAddButton,'keydown', this.onKeyDownAdd.bind(this));
        this.domUtils.addEventListener(this.elements.currentRemoveButton,'keydown', this.onKeyDownRemove.bind(this));
    }


    protected onKeyDownAdd(e: KeyboardEvent) {
        if (e.keyCode === keyCodes.ENTER) {
            this.clone(e);
        }
    }

    protected onKeyDownRemove(e: KeyboardEvent) {
        if (e.keyCode === keyCodes.ENTER) {
            this.remove(e);
        }
    }
    
    protected searchTermsAligner = (searchTermRows: HTMLElement[])=>{
        searchTermRows.forEach((searchTerm, index)=>{
        const contextSearchLabel= searchTerm?.querySelector('.advanced-search__select')?.previousElementSibling;
        const TermSearchLabel= searchTerm?.querySelector('.advanced-search__search-term') ? searchTerm?.querySelector('.advanced-search__search-term')?.previousElementSibling: searchTerm?.querySelector('.advanced-search__input')?.previousElementSibling;
        const removeBtn= searchTerm?.querySelector('.advanced-search__search-in__remove');
        contextSearchLabel? contextSearchLabel.innerHTML= `Context for search term ${index + 1}`: '';
        TermSearchLabel? TermSearchLabel.innerHTML= `Search term ${index + 1}`: '';    
        removeBtn?.setAttribute('aria-label', `${this.texts.remove} ${index + 1}`);
        removeBtn?.setAttribute('title', `${this.texts.remove} ${index + 1}`);
    })
    }

    /**
     * clone the search in field and set the events
     */
    protected clone(e: Event) {
        e.stopPropagation();
        e.preventDefault();
        const div = document.createElement('div');
        div.innerHTML = this.elements.searchInFieldWrapper[0]?.outerHTML;
        const searchInField = this.domUtils.getElement(this.selectors.searchInFieldWrapper.selector, div);
        const clonedInput = searchInField.querySelector('input');
        const clonedSelectInput : HTMLSelectElement = searchInField.querySelector('.advanced-search__search-in__area');
        clonedInput.removeAttribute('value');
        if(clonedSelectInput?.options?.length){
            clonedSelectInput.options[0].selected = true;
        }
        this.wrapper.insertAdjacentElement('beforeend', searchInField);
        this.elements.searchInField = searchInField;
        this.addEventListeners();
        this.toggleButtons();

        const fieldWrapper= this.elements.searchInField;
        const selectFieldWrapper= fieldWrapper?.querySelector('select[id^="searchArea"]') as HTMLElement;
        selectFieldWrapper?.focus();
    }

    /**
     * remove the current tabular item
     *
     * @param e
     */
    remove(e: Event) {
        e.stopPropagation();
        e.preventDefault();
        const searchTermWrapper= (e.currentTarget as HTMLElement).closest(this.selectors.searchInFieldWrapper.selector); 
        const searchTerms= this.elements.wrapper.querySelectorAll(this.selectors.searchInFieldWrapper.selector)
        const filteredTermRows= Array.from(searchTerms)?.filter((searchTerm) => searchTerm !== searchTermWrapper);

        let focusedSearchTerm: HTMLElement; 
        let focusedSearchActionBtn: HTMLElement;

        searchTerms?.forEach((searchTerm, index)=> {
            if (searchTerm === searchTermWrapper){
                const targetElement= index === 0 ? searchTerms[index+1] : searchTerms[index-1];
                focusedSearchTerm= targetElement as HTMLElement;
                focusedSearchActionBtn = focusedSearchTerm?.querySelector('.advanced-search__search-in__remove:not(.hidden)') ? focusedSearchTerm?.querySelector('.advanced-search__search-in__remove:not(.hidden)') : focusedSearchTerm?.querySelector('.advanced-search__search-in__add:not(.hidden)');
                focusedSearchActionBtn?.setAttribute('tabindex', '0');
            }
            else {
                searchTerm?.removeAttribute('tabindex');
            }
        })
        
        focusedSearchActionBtn?.focus();
        searchTermWrapper?.remove();
        this.searchTermsAligner(filteredTermRows as HTMLElement[]);
        this.toggleButtons();
    }

    /**
     * hide/show the action buttons depending on the item in the list,
     * and change the fields names
     */

    toggleButtons() {
        const items = this.elements.searchInFields;
        items.forEach((item, key) => {
            this.changeFieldAttributes(item, key);
            const add = this.elements.getAddButton(item);
            const remove = this.elements.getRemoveButton(item);
            const contextSearchLabel= item?.querySelector('.advanced-search__select')?.previousElementSibling;
            const TermSearchLabel= item?.querySelector('.advanced-search__search-term') ? item?.querySelector('.advanced-search__search-term')?.previousElementSibling: item?.querySelector('.advanced-search__input')?.previousElementSibling;

            if (key === items.length - 1) {
                remove?.classList.add(this.classList.hide);
                add?.classList.remove(this.classList.hide);
                contextSearchLabel? contextSearchLabel.innerHTML= `Context for search term ${key + 1}`: '';
                TermSearchLabel? TermSearchLabel.innerHTML= `Search term ${key + 1}`: '';
                remove?.setAttribute('aria-label', `${this.texts.remove} ${key + 1}`);
                remove?.setAttribute('title', `${this.texts.remove} ${key + 1}`);
                add?.setAttribute('aria-label', this.texts.add);
            } else {
                add?.classList.add(this.classList.hide);
                remove?.classList.remove(this.classList.hide);
            }
        });
    }

    /**
     * changes the id,name for each tabular search in field
     * @param item
     * @param key
     */
    changeFieldAttributes(item: HTMLElement, key: number) {
        const dropdown = this.domUtils.getElement<HTMLInputElement>(this.selectors.typeField, item);
        const input = this.domUtils.getElement<HTMLInputElement>(this.selectors.keywordField, item);
        const labels = this.domUtils.getElements("label", item);
        const number = key + 1;
        input.name = this.fieldNames.input + number;
        dropdown.name = this.fieldNames.dropdown + number;
        input.id = this.classList.keywordField + number;
        dropdown.id = this.classList.typeField + number;
        labels.forEach((label: HTMLElement) => {
            const forAttr = label.getAttribute("for");
            if (forAttr.includes("text")) {
                label.setAttribute("for", `text${number}`);
            } else {
                label.setAttribute("for", `searchArea${number}`);
            }
        });
    }

}

export default AdvancedSearchTabular;

export {
    Elements as AdvancedSearchTabularElements,
    Selectors as AdvancedSearchTabularSelectors,
    ClassList as AdvancedSearchTabularClassList,
    Texts as AdvancedSearchTabularTexts
}